import {
  Client,
  FileUpload,
  LargeFileUploadSession,
  LargeFileUploadTask,
  Range,
} from '@microsoft/microsoft-graph-client';
import { DriveItem } from '@microsoft/microsoft-graph-types';
import {
  DocumentFile,
  DocumentItem,
} from '../features/Collaboration/SharedDocuments/DocumentFile';
import { DocumentFolder } from '../features/Collaboration/SharedDocuments/DocumentFolder';
import { loginRequest, msalInstance } from '../shared/authentication-config';

const MAX_DAYS_AGE_FOR_LATEST_ACTIVITY = 14;

const client = Client.init({
  authProvider: async (cb) => {
    const { accessToken } = await msalInstance.acquireTokenSilent(loginRequest);
    cb(null, accessToken);
  },
});

const siteIdCache = new Map<string, string | undefined>();

type PrivateFolder = {
  lastModifiedDateTime: string;
  name: string;
  siteId: string;
  webUrl: string;
};

export const sharedDocumentsClient = {
  uploadFile: async (
    file: File,
    teamId: string,
    folder?: string,
    drive?: string,
    onProgress?: (progress: number) => void,
  ) => {
    const filePath = folder ? `${folder}/${file.name}` : file.name;
    const path = await sharedDocumentsClient.getCurrentFolder(
      teamId,
      filePath,
      drive,
    );

    const fileUpload = new FileUpload(file, file.name, file.size);

    const requestUrl = `${path}/createUploadSession`;

    const payload = {
      item: {
        '@microsoft.graph.conflictBehavior': 'rename',
        name: file.name,
      },
    };

    const uploadSession: LargeFileUploadSession =
      await LargeFileUploadTask.createUploadSession(
        client,
        requestUrl,
        payload,
      );

    const uploadTask = new LargeFileUploadTask(
      client,
      fileUpload,
      uploadSession,
      {
        rangeSize: 1024 * 1024,
        uploadEventHandlers: {
          progress: (range?: Range) => {
            if (onProgress && range) {
              const bytesUploaded = range.maxValue + 1;
              const fileTotal = file.size;
              const progress = Math.round((bytesUploaded / fileTotal) * 100);
              onProgress(progress);
            }
          },
        },
      },
    );

    await uploadTask.upload();
  },

  getPrivateFolders: async (teamId: string) => {
    const channelsResponse = await client
      .api(`/teams/${teamId}/channels`)
      .get();

    const privateChannels = channelsResponse.value.filter(
      (channel: { membershipType?: string }) =>
        channel?.membershipType === 'private',
    );

    const privateFolderResults = await Promise.allSettled<PrivateFolder>(
      privateChannels.map(
        async (channel: {
          id: string;
          displayName: string;
        }): Promise<PrivateFolder> => {
          const folderInfo = await client
            .api(`/teams/${teamId}/channels/${channel.id}/filesFolder`)
            .get();

          const webUrl = folderInfo.webUrl;

          const url = new URL(webUrl);
          const hostname = url.hostname;
          const pathnameParts = url.pathname.split('/');
          const sitePath = `/sites/${pathnameParts[2]}`;

          const site = await client.api(`/sites/${hostname}:${sitePath}`).get();

          return {
            lastModifiedDateTime: site.lastModifiedDateTime,
            name: channel.displayName,
            siteId: site.id,
            webUrl: folderInfo.webUrl,
          };
        },
      ),
    );

    const privateFolders: PrivateFolder[] = privateFolderResults
      .filter(
        (result): result is PromiseFulfilledResult<PrivateFolder> =>
          result.status === 'fulfilled',
      )
      .map((result) => result.value);

    return privateFolders;
  },

  getTeamsSiteId: async (teamId: string) => {
    const { id: siteId } = await client
      .api(`/groups/${teamId}/sites/root`)
      .select('id')
      .get();

    siteIdCache.set(teamId, siteId);
    return siteId;
  },

  getCurrentFolder: async (
    teamId: string,
    subRessource?: string,
    privateSiteId?: string,
  ) => {
    const addition = subRessource ? `:/${subRessource}:` : '';

    const siteId =
      privateSiteId ||
      siteIdCache.get(teamId) ||
      (await sharedDocumentsClient.getTeamsSiteId(teamId));

    return `/sites/${siteId}/drive/root${addition}`;
  },

  getFolderContents: async (
    teamId: string,
    folder?: string,
    siteId?: string,
  ) => {
    const path = await sharedDocumentsClient.getCurrentFolder(
      teamId,
      folder,
      siteId,
    );

    const { value: items }: { value: DriveItem[] } = await client
      .api(`${path}/children`)
      .get();

    const privateFolders = folder
      ? []
      : await sharedDocumentsClient.getPrivateFolders(teamId);

    return [
      ...privateFolders.map(
        (folder) =>
          new DocumentFolder({
            id: folder.siteId,
            name: folder.name,
            lastModified: folder.lastModifiedDateTime
              ? new Date(folder.lastModifiedDateTime)
              : null,
            owner: null,
            url: folder.webUrl,
            siteId: folder.siteId,
          }),
      ),
      ...items.map((item) => {
        const mapped: DocumentItem = {
          id: item.id ?? null,
          name: item.name ?? null,
          lastModified: item.lastModifiedDateTime
            ? new Date(item.lastModifiedDateTime)
            : null,
          owner: item.createdBy?.user?.displayName ?? null,
          url: item.webUrl ?? null,
        };
        return item.file
          ? new DocumentFile({
              ...mapped,
              mimeType: item.file.mimeType ?? null,
            })
          : new DocumentFolder({ ...mapped, siteId: null });
      }),
    ];
  },

  getLastModifiedFiles: async (teamId: string, maxResults = 4) => {
    const retrieveAllItems = async (
      teamId: string,
      folder = '',
      siteId?: string,
    ): Promise<DocumentFile[]> => {
      const items = await sharedDocumentsClient.getFolderContents(
        teamId,
        folder,
        siteId,
      );

      const children = await Promise.all(
        items.map(async (item) =>
          item instanceof DocumentFolder
            ? await retrieveAllItems(
                teamId,
                `${folder}/${item.name}`,
                item.siteId ?? siteId ?? undefined,
              )
            : item,
        ),
      );

      return children.flat();
    };

    const items = await retrieveAllItems(teamId);

    const hasValidLastModified = (
      item: DocumentFile,
    ): item is DocumentFile & { lastModified: Date } => {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - MAX_DAYS_AGE_FOR_LATEST_ACTIVITY);
      return item.lastModified !== null && item.lastModified >= cutoff;
    };

    return items
      .filter(hasValidLastModified)
      .sort((a, b) => b.lastModified.getTime() - a.lastModified.getTime())
      .slice(0, maxResults);
  },

  deleteFile: async (
    fileName: string,
    teamId: string,
    folder?: string,
    siteId?: string,
  ) => {
    const filePath = folder ? `${folder}/${fileName}` : fileName;
    const path = await sharedDocumentsClient.getCurrentFolder(
      teamId,
      filePath,
      siteId,
    );
    return await client.api(path).delete();
  },
};
